import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Users, Briefcase, MessageSquare, Receipt, TrendingUp, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/leads").then((r) => setRecentLeads(r.data.slice(0, 5))).catch(() => {});
  }, []);

  return (
    <DashboardLayout role="admin" title="Overview" subtitle="Welcome back. Here's the pulse of Coastal Bend Contractor Co.">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="New Leads" value={stats?.leads_new ?? "—"} icon={MessageSquare} accent />
        <Stat label="Total Leads" value={stats?.leads_total ?? "—"} icon={TrendingUp} />
        <Stat label="Projects" value={stats?.projects_total ?? "—"} icon={Briefcase} />
        <Stat label="Clients" value={stats?.clients_total ?? "—"} icon={Users} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold">Recent Leads</h2>
            <Link to="/admin/leads" className="text-sm flex items-center gap-1" style={{color:'var(--accent)'}}>View all <ArrowUpRight size={13}/></Link>
          </div>
          {recentLeads.length === 0 ? (
            <div className="text-sm py-8 text-center" style={{color:'var(--text-muted)'}}>No leads yet.</div>
          ) : (
            <div className="divide-y divide-white/5">
              {recentLeads.map((l) => (
                <div key={l.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{l.name}</div>
                    <div className="text-xs truncate" style={{color:'var(--text-muted)'}}>
                      {l.email} · {l.service?.replace('_',' ')}
                    </div>
                  </div>
                  <span className={`badge ${l.status === 'new' ? '' : 'badge-muted'}`}>{l.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card p-6">
          <h2 className="font-display text-xl font-bold mb-1">Revenue (paid)</h2>
          <p className="text-xs mb-5" style={{color:'var(--text-muted)'}}>Sum of paid invoices</p>
          <div className="font-display text-4xl font-bold" style={{color:'var(--accent)'}}>{money(stats?.revenue || 0)}</div>
          <div className="divider my-5"/>
          <div className="flex items-center justify-between text-sm">
            <span style={{color:'var(--text-muted)'}}>Unpaid invoices</span>
            <span className="font-semibold">{stats?.invoices_unpaid ?? "—"}</span>
          </div>
          <div className="flex items-center justify-between text-sm mt-2">
            <span style={{color:'var(--text-muted)'}}>In-progress projects</span>
            <span className="font-semibold">{stats?.projects_in_progress ?? "—"}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickLink to="/admin/leads" icon={MessageSquare} label="Manage Leads" />
        <QuickLink to="/admin/invoices" icon={Receipt} label="Create Invoice" />
        <QuickLink to="/admin/projects" icon={Briefcase} label="Add Project" />
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider mb-3" style={{color:'var(--text-muted)'}}>
        <Icon size={13} style={{color: accent ? 'var(--accent)' : 'var(--text-muted)'}}/> {label}
      </div>
      <div className="font-display text-3xl font-bold" style={accent ? {color:'var(--accent)'} : {}}>{value}</div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label }) {
  return (
    <Link to={to} className="card p-5 flex items-center justify-between hover:translate-y-[-2px] transition-transform">
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-md flex items-center justify-center" style={{background:'rgba(212,163,115,0.1)'}}>
          <Icon size={16} style={{color:'var(--accent)'}}/>
        </span>
        <span className="font-medium">{label}</span>
      </div>
      <ArrowUpRight size={16} style={{color:'var(--text-muted)'}}/>
    </Link>
  );
}
